import React, { useState, useEffect } from "react";
import {
  Grid,
  useTheme,
  Typography,
  Box,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  IconButton,
  CircularProgress,
  Fade,
  Button,
  Fab,
  styled,
  keyframes,
  useMediaQuery,
} from "@mui/material";
import {
  Refresh,
  ErrorOutline,
  Tune,
  AddCircleOutline,
} from "@mui/icons-material";
import SearchBar from "../components/SearchBar";
import FilterModal from "../components/FilterModal";
import SortButton from "../components/SortButton";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import { useQuery, gql, useMutation } from "@apollo/client";
import LoadingPage from "./LoadingPage";
import { useTranslation } from "react-i18next";
import FilteredAttendees from "../components/FilteredAttendees";
import FilterButton from "../components/FilterButton";
import ExhibitorForm from "../components/AddExhibitorsForm";
import { UPLOAD_IMAGES_API_URL } from "../constant";
import useUserData from "../hooks/useUserData";
import FilteredExhibitors from "../components/FilteredExhibitors";

const GET_ALL_EXHIBITORS = gql`
  query GetAllExhibitors($roleType: String) {
    getAllExhibitors(roleType: $roleType) {
      id
      company_name
      email
      country
      website
      stallNo
      fascia_board
      brands
      profile_pic
      company_profile
      team_members
      created
      isConnected
    }
  }
`;
const UPDATE_EXHIBITOR = gql`
  mutation UpdateExhibitor($id: ID!, $updateData: String!) {
    updateExhibitor(id: $user_id, updateData: $updateData) {
      id
      success
      message
    }
  }
`;
const ADD_EXHIBITOR = gql`
  mutation AddExhibitor($jsonData: String!) {
    addExhibitor(jsonData: $jsonData) {
      id
      success
      message
    }
  }
`;
const DELETE_EXHIBITOR = gql`
  mutation UpdateExhibitor($id: ID!) {
    updateExhibitor(id: $user_id) {
      id
      success
      message
    }
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const ShinyFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  animation: `${shimmer} 50s infinite linear`,
  backgroundSize: "15% auto",
  transition: "transform 0.3s, box-shadow 0.3s",
  backdropFilter: "blur(10px)",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: theme.shadows[10],
  },
}));

const ExhibitorList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [exhibitors, setExhibitors] = useState([]);
  const [filteredExhibitors, setFilteredExhibitors] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const { data: user_data, refetch: userRefetch } = useUserData();
  let userdata = user_data?.me;
  if (typeof user_data?.me === "string") {
    userdata = JSON.parse(user_data?.me);
  }

  const user = userdata;
  const [filters, setFilters] = useState({
    role: [],
    company: [],
    designation: [],
    country: [],
  });

  const [formOpen, setFormOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    company_name: "",
    company_profile: "",
    profile_pic: null,
  });
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [addExhibitor, { loading: uploadLoading, error: uploadError }] =
    useMutation(ADD_EXHIBITOR);
  const handleAddProduct = async (formData) => {
    await addExhibitor({
      variables: {
        jsonData: JSON.stringify(formData),
      },
    })
      .then(async (res) => {
        if (res?.data?.addExhibitor?.success) {
          const exhibitor_id = res?.data?.addExhibitor?.id;
          const imageFile = formData.profile_pic;
          const formDataImage = new FormData();
          formDataImage.append("image", imageFile);

          const imageResponse = await fetch(
            `${UPLOAD_IMAGES_API_URL}/upload/user/${user.eventId}/exhibitors/${exhibitor_id}`,
            {
              method: "POST",
              body: formDataImage, // Use FormData here
            }
          );

          const imageData = await imageResponse.json();
          if (imageData.success) {
            setSuccessMessage(t("productAddedSuccessfully"));
            setTimeout(() => {
              setSuccessMessage("");
              setFormOpen(false);
              refetch();
            }, 2000);
          } else {
            setErrorMessage(t("errorUploadingImage"));
            setTimeout(() => {
              setErrorMessage("");
            }, 2000);
          }
        } else {
          setErrorMessage(t("errorAddingProduct"));
          setTimeout(() => {
            setErrorMessage("");
          }, 2000);
        }
      })
      .catch(() => {
        setErrorMessage(t("errorAddingProduct"));
        setTimeout(() => {
          setErrorMessage("");
        }, 2000);
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const roleType = "exhibitors";
  const [updateExhibitor] = useMutation(UPDATE_EXHIBITOR);
  const [deleteExhibitor] = useMutation(DELETE_EXHIBITOR);

  const { loading, error, data, refetch } = useQuery(GET_ALL_EXHIBITORS, {
    variables: { roleType },
    notifyOnNetworkStatusChange: true,
    skip: !roleType,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.getAllExhibitors) {
      // Create a safe sorted copy without mutating original data
      const filteredData = [...(data.getAllExhibitors || [])]
        // Add null safety for company_name comparison
        .sort((a, b) =>
          (a.company_name || "").localeCompare(b.company_name || "")
        );

      // console.log("Sorted exhibitors:", filteredData);

      // Update both state arrays with the sorted data
      setExhibitors(filteredData);
      setFilteredExhibitors(filteredData);
    } else {
      // Clear states if no data
      setExhibitors([]);
      setFilteredExhibitors([]);
    }
  }, [data]);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortOption, setSortOption] = useState("");

  useEffect(() => {
    let filtered = [...exhibitors];

    // Apply search first
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((exhibitor) =>
        exhibitor.company_name.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    // if (filters.role.length > 0) {
    //   filtered = filtered.filter(a => filters.role.includes(a.role));
    // }
    if (filters.company.length > 0) {
      filtered = filtered.filter((a) =>
        filters.company.includes(a.company_name)
      );
    }
    if (filters.designation.length > 0) {
      filtered = filtered.filter((a) =>
        filters.designation.includes(a.company_name)
      );
    }
    if (filters.country.length > 0) {
      filtered = filtered.filter((a) => filters.country.includes(a.country));
    }

    // Apply sorting
    if (sortOption) {
      filtered.sort((a, b) => {
        let field;
        if (sortOption === "name") {
          field = "company_name";
        } else {
          field = "country";
        }
        return a[field].localeCompare(b[field]);
      });
    }

    setFilteredExhibitors(filtered);
    setCurrentPage(1);
  }, [exhibitors, searchQuery, filters, sortOption]);

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setFilterModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchQuery(event);
  };

  const handleFilter = () => {
    setFilterModalOpen(true);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (option) => {
    setSortOption(option);
    setSortAnchorEl(null);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExhibitors = filteredExhibitors.slice(startIndex, endIndex);
  const handleRetry = () => {
    refetch();
  };

  const handleEdit = (exhibitor) => {
    setNewProduct(exhibitor);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await deleteExhibitor({ variables: { id } });
      if (data.deleteExhibitor.success) {
        setSuccessMessage(t("exhibitorDeletedSuccessfully"));
        refetch();
      } else {
        setErrorMessage(t("errorDeletingExhibitor"));
      }
    } catch (error) {
      setErrorMessage(t("errorDeletingExhibitor"));
    }

    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  if (error) {
    return (
      <>
        <BackWithBreadcrumbAppbar
          links={[{ label: t("dashboard"), href: "/" }]}
          currentPage={t("exhibitors")}
        />
        <Box sx={{ p: 3, height: "100vh" }}>
          <Alert
            severity="error"
            sx={{
              mt: 4,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
            }}
            action={
              <IconButton color="inherit" onClick={handleRetry}>
                <Refresh />
              </IconButton>
            }
          >
            <AlertTitle>{t("Error")}</AlertTitle>
            {t("404")} ({error?.message || "Something went wrong"})
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: t("dashboard"), href: "/" }]}
        currentPage={t("exhibitors")}
      />
      <Box
        sx={{
          padding: theme.spacing(1),
          position: "relative",
          maxWidth: 1600,
          margin: "0 auto",
        }}
      >
        <Fade in={!loading} timeout={300}>
          <Box>
            <Typography
              variant="h3"
              align="center"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                my: 3,
                textShadow: `0 2px 4px ${theme.palette.action.hover}`,
              }}
            >
              {t("exhibitorsList")}
            </Typography>

            <Grid
              container
              spacing={1.5}
              alignItems="center"
              sx={{
                position: "sticky",
                top: isMobile ? 100 : 130,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                py: 2,
                px: 1,
                boxShadow: `0 2px 8px ${
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.3)"
                    : "rgba(0,0,0,0.1)"
                }`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                marginBottom: 3,
                transition: "all 0.2s ease",
              }}
            >
              {/* Search Bar */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <SearchBar
                  role="exhibitor"
                  onSearch={handleSearch}
                  attendees={exhibitors}
                  placeholder={t("searchExhibitors")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: theme.transitions.create([
                        "border-color",
                        "box-shadow",
                      ]),
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused": {
                        boxShadow: `${theme.shadows[2]}`,
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Filter & Sort Controls */}
              <Grid
                item
                xs={8}
                md={4}
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                {/* Filter Button */}
                <Box sx={{ flex: 1 }}>
                  <FilterButton
                    onFilter={handleFilter}
                    badgeContent={Object.values(filters).flat().length}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Sort Button */}
                <Box sx={{ flex: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSortClick}
                    startIcon={<Tune fontSize="small" />}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t("sort")}
                  </Button>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={handleSortClose}
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        minWidth: 150,
                        mt: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    {["name", "country"].map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => handleSortSelect(option)}
                        selected={sortOption === option}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: theme.palette.action.selected,
                            fontWeight: 500,
                          },
                          py: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        {t(option)}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Grid>

              {/* Items Per Page Selector */}
              <Grid
                item
                xs={4}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: { xs: 80, sm: 150 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <InputLabel shrink>{t("itemsPerPage")}</InputLabel>
                  <Select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    label={t("itemsPerPage")}
                    sx={{
                      "& .MuiSelect-select": {
                        py: 1.1,
                        pl: 2,
                      },
                    }}
                  >
                    {[10, 25, 50, 100].map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        sx={{
                          borderRadius: 1,
                          m: 0.5,
                          "&.Mui-selected": {
                            backgroundColor: `${theme.palette.action.selected} !important`,
                          },
                        }}
                      >
                        {value}
                      </MenuItem>
                    ))}
                    <MenuItem
                      value={exhibitors.length}
                      sx={{
                        borderRadius: 1,
                        m: 0.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {t("all")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FilteredExhibitors
              role="exhibitor"
              user={user}
              refresh={refetch}
              filteredExhibitors={filteredExhibitors}
              paginatedExhibitors={paginatedExhibitors}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              setFilteredExhibitors={setFilteredExhibitors}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            <FilterModal
              open={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              onApply={handleFilterApply}
              filters={filters}
              attendees={exhibitors}
              roleName="exhibitor"
            />
          </Box>
        </Fade>

        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              zIndex: theme.zIndex.modal,
            }}
          >
            <CircularProgress
              size={80}
              thickness={2}
              sx={{
                color: theme.palette.primary.main,
                animationDuration: "800ms",
              }}
            />
          </Box>
        )}
      </Box>
      {user?.email === "mohd.yahiya7@gmail.com" && (
        <ShinyFab color="primary" onClick={() => setFormOpen(true)}>
          <AddCircleOutline />
        </ShinyFab>
      )}

      <ExhibitorForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleAddProduct}
        product={newProduct}
        setProduct={setNewProduct}
        uploading={uploading}
        errorMessage={errorMessage}
        successMessage={successMessage}
        user={user}
        uploadLoading={uploadLoading}
        uploadError={uploadError}
        mode="create"
      />
    </>
  );
};

export default ExhibitorList;
